// Helpers puros de la dirección de usuario que el admin lee del GET y manda en
// el PUT/POST de /admin/api/v1/users. México y Argentina exigen una dirección
// completa (validación Zod en el backend); el resto de los países no.

const MX_ISO = 'MX';
const AR_ISO = 'AR';
const MX_POSTAL_CODE = /^\d{5}$/;
// Código postal argentino: el antiguo de 4 dígitos ("1425") o el CPA ("C1425ABC").
const AR_POSTAL_CODE = /^(\d{4}|[A-Za-z]\d{4}[A-Za-z]{3})$/;
const TEXT_FIELDS = ['street', 'number', 'extra', 'neighborhood', 'postalCode', 'references'];

const clean = value => (value === null || value === undefined ? '' : String(value).trim());

const toPlace = place =>
  place !== null && typeof place === 'object'
    ? { code: clean(place.code), name: clean(place.name) }
    : { code: '', name: clean(place) };

export function isMexico(country) {
  return clean(country).toUpperCase() === MX_ISO;
}

export function isArgentina(country) {
  return clean(country).toUpperCase() === AR_ISO;
}

// Qué formulario de dirección usa un país: 'MX' (SEPOMEX), 'AR' (provincias y
// localidades) o 'CL', el de regiones y comunas de Chile que hoy sirve también
// a Perú, Puerto Rico y a un país sin definir.
export function addressFormKind(country) {
  if (isMexico(country)) return 'MX';
  if (isArgentina(country)) return 'AR';
  return 'CL';
}

export function emptyAddress() {
  return {
    street: '',
    number: '',
    extra: '',
    neighborhood: '',
    postalCode: '',
    references: '',
    region: { code: '', name: '' },
    commune: { code: '', name: '' }
  };
}

// Lleva cualquier shape de dirección (GET con claves ausentes, region/commune
// como string u objeto) a la forma completa, con string vacío donde no hay valor.
export function normalizeAddress(address) {
  const source = address || {};
  const normalized = emptyAddress();
  TEXT_FIELDS.forEach(field => {
    normalized[field] = clean(source[field]);
  });
  normalized.region = toPlace(source.region);
  normalized.commune = toPlace(source.commune);
  return normalized;
}

// Misma regla que el backend: todo null, '' o solo espacios = no hay dirección.
export function isAddressEmpty(address) {
  const a = normalizeAddress(address);
  return (
    TEXT_FIELDS.every(field => !a[field]) &&
    !a.region.code &&
    !a.region.name &&
    !a.commune.code &&
    !a.commune.name
  );
}

export function addressChanged(initial, current) {
  return JSON.stringify(normalizeAddress(initial)) !== JSON.stringify(normalizeAddress(current));
}

// Etiquetas de los campos obligatorios de México que faltan, en el orden del formulario.
export function missingMexicanAddressFields(address) {
  const a = normalizeAddress(address);
  const missing = [];
  if (!a.region.code || !a.region.name) missing.push('Estado');
  if (!a.commune.code || !a.commune.name) missing.push('Municipio');
  if (!a.neighborhood) missing.push('Colonia');
  if (!MX_POSTAL_CODE.test(a.postalCode)) missing.push('Código postal');
  if (!a.street) missing.push('Calle');
  if (!a.number) missing.push('Número exterior');
  return missing;
}

export function isValidMexicanPostalCode(value) {
  return MX_POSTAL_CODE.test(clean(value));
}

export function isValidArgentinePostalCode(value) {
  return AR_POSTAL_CODE.test(clean(value));
}

// Etiquetas de los campos de Argentina que faltan, en el orden del formulario.
// Obligatorios: provincia, localidad, calle y altura. El código postal es opcional,
// pero si se escribió debe tener un formato válido.
export function missingArgentineAddressFields(address) {
  const a = normalizeAddress(address);
  const missing = [];
  if (!a.region.code || !a.region.name) missing.push('Provincia');
  if (!a.commune.code || !a.commune.name) missing.push('Localidad');
  if (!a.street) missing.push('Calle');
  if (!a.number) missing.push('Número (altura)');
  if (a.postalCode && !AR_POSTAL_CODE.test(a.postalCode)) missing.push('Código postal');
  return missing;
}

// Body de `address`. México y Argentina mandan colonia o barrio, código postal y
// referencias; para otros países el backend conserva esas claves si no vienen.
export function toAddressPayload(address, country) {
  const a = normalizeAddress(address);
  const orNull = value => value || null;
  const kind = addressFormKind(country);
  const payload = {
    street: a.street,
    number: a.number,
    // México y Argentina: null borra el número interior guardado (semántica acordada con el backend).
    extra: kind === 'CL' ? a.extra : orNull(a.extra),
    region: { code: orNull(a.region.code), name: a.region.name },
    commune: { code: orNull(a.commune.code), name: a.commune.name }
  };
  if (kind === 'MX') {
    return {
      ...payload,
      neighborhood: a.neighborhood,
      postalCode: a.postalCode,
      references: orNull(a.references)
    };
  }
  if (kind === 'AR') {
    // Todo lo opcional vacío va como null para que el backend lo borre.
    return {
      ...payload,
      neighborhood: orNull(a.neighborhood),
      postalCode: orNull(a.postalCode),
      references: orNull(a.references)
    };
  }
  return payload;
}
